import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormGroup,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { updatePlan, getPlanById } from '../services/subscriptionPlanService';

/**
 * Feature categories with their features
 * - Loyalty points, WhatsApp bill sharing, E‑commerce website
 * - Web app / Android app: which platforms the plan allows (used for login restriction)
 */
const FEATURE_CATEGORIES = {
  core: {
    label: 'Features',
    features: [
      {
        id: 'loyalty_points',
        label: 'Loyalty points',
        description: 'Earn and redeem loyalty points in POS and reports',
      },
      {
        id: 'whatsapp_bill_share',
        label: 'WhatsApp bill sharing',
        description: 'Share bills via WhatsApp from POS and print screens',
      },
      {
        id: 'ecommerce_website',
        label: 'E‑commerce website',
        description: 'E‑commerce domain and website related settings',
      },
      {
        id: 'ai_assistant',
        label: 'AI Assistant (Ask Gemini)',
        description: 'Dashboard AI insights powered by Gemini',
      },
      {
        id: 'shopify_integration',
        label: 'Shopify integration',
        description: 'Connect Shopify store and sync online orders',
      },
      {
        id: 'webapp',
        label: 'Web app',
        description: 'Users can sign in and use the web application',
      },
      {
        id: 'android_app',
        label: 'Android app',
        description: 'Users can sign in and use the Android mobile app',
      },
    ],
  },
};

/**
 * Edit Subscription Page
 * Edit existing subscription plans
 */
const EditSubscriptionPage = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    planCode: '',
    name: '',
    description: '',
    priceMonthly: '',
    priceYearly: '',
    currency: 'USD',
    trialDays: 14,
    maxUsers: '',
    maxLocations: '',
    maxProducts: '',
    maxMonthlyOrders: '',
    maxStorageGb: '',
    isPopular: false,
    sortOrder: '',
  });

  // Unlimited flags for limits
  const [unlimitedFlags, setUnlimitedFlags] = useState({
    maxUsers: false,
    maxLocations: false,
    maxProducts: false,
    maxMonthlyOrders: false,
    maxStorageGb: false,
  });

  // Features state
  const [features, setFeatures] = useState({});

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Load plan data
  useEffect(() => {
    loadPlanData();
  }, [planId]);

  const loadPlanData = async () => {
    setLoading(true);
    setError(null);
    try {
      const plan = await getPlanById(planId);
      
      // Populate form with plan data
      setFormData({
        planCode: plan.planCode || '',
        name: plan.name || '',
        description: plan.description || '',
        priceMonthly: plan.priceMonthly?.toString() || '',
        priceYearly: plan.priceYearly?.toString() || '',
        currency: plan.currency || 'USD',
        trialDays: plan.trialDays || 14,
        maxUsers: plan.maxUsers === -1 ? '' : (plan.maxUsers?.toString() || ''),
        maxLocations: plan.maxLocations === -1 ? '' : (plan.maxLocations?.toString() || ''),
        maxProducts: plan.maxProducts === -1 ? '' : (plan.maxProducts?.toString() || ''),
        maxMonthlyOrders: plan.maxMonthlyOrders === -1 ? '' : (plan.maxMonthlyOrders?.toString() || ''),
        maxStorageGb: plan.maxStorageGb === -1 ? '' : (plan.maxStorageGb?.toString() || ''),
        isPopular: plan.isPopular || false,
        sortOrder: plan.sortOrder?.toString() || '',
      });

      // Set unlimited flags
      setUnlimitedFlags({
        maxUsers: plan.maxUsers === -1,
        maxLocations: plan.maxLocations === -1,
        maxProducts: plan.maxProducts === -1,
        maxMonthlyOrders: plan.maxMonthlyOrders === -1,
        maxStorageGb: plan.maxStorageGb === -1,
      });

      // Set features (normalize to our known flags including webapp / android_app)
      if (plan.features && typeof plan.features === 'object') {
        const f = plan.features || {};
        setFeatures({
          loyalty_points: !!f.loyalty_points,
          whatsapp_bill_share: !!f.whatsapp_bill_share,
          ecommerce_website: !!f.ecommerce_website,
          ai_assistant: !!f.ai_assistant,
          shopify_integration: !!f.shopify_integration,
          webapp: !!f.webapp,
          android_app: !!f.android_app,
        });
      } else {
        setFeatures({
          loyalty_points: false,
          whatsapp_bill_share: false,
          ecommerce_website: false,
          ai_assistant: false,
          shopify_integration: false,
          webapp: false,
          android_app: false,
        });
      }
    } catch (err) {
      console.error('Error loading plan:', err);
      setError(err.message || 'Failed to load plan data');
      enqueueSnackbar(err.message || 'Failed to load plan data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleChange = (field) => (event) => {
    let value = event.target.value;
    
    // For number fields, keep as string but ensure it's valid
    if (field === 'sortOrder' || field === 'trialDays') {
      // Allow empty string or valid number
      if (value === '' || (!isNaN(value) && value >= 0)) {
        setFormData(prev => ({
          ...prev,
          [field]: value === '' ? '' : value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle unlimited toggle
  const handleUnlimitedToggle = (field) => (event) => {
    const checked = event.target.checked;
    setUnlimitedFlags(prev => ({
      ...prev,
      [field]: checked
    }));
    
    // Set to -1 if unlimited, clear if not
    setFormData(prev => ({
      ...prev,
      [field]: checked ? -1 : ''
    }));
  };

  // Handle feature toggle
  const handleFeatureToggle = (featureId) => (event) => {
    setFeatures(prev => ({
      ...prev,
      [featureId]: event.target.checked
    }));
  };

  // Handle select all features in category
  const handleSelectAllCategory = (categoryId) => () => {
    const category = FEATURE_CATEGORIES[categoryId];
    const allSelected = category.features.every(f => features[f.id]);
    
    const newFeatures = { ...features };
    category.features.forEach(f => {
      newFeatures[f.id] = !allSelected;
    });
    setFeatures(newFeatures);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required';
    } else if (formData.name.length < 3 || formData.name.length > 100) {
      newErrors.name = 'Plan name must be between 3 and 100 characters';
    }

    if (!formData.priceMonthly || parseFloat(formData.priceMonthly) < 0) {
      newErrors.priceMonthly = 'Monthly price is required and must be >= 0';
    }

    if (!formData.priceYearly || parseFloat(formData.priceYearly) < 0) {
      newErrors.priceYearly = 'Yearly price is required and must be >= 0';
    }

    if (formData.trialDays < 0) {
      newErrors.trialDays = 'Trial days must be >= 0';
    }

    // Validate limits (must be >= -1)
    const limitFields = ['maxUsers', 'maxLocations', 'maxProducts', 'maxMonthlyOrders', 'maxStorageGb'];
    limitFields.forEach(field => {
      if (formData[field] !== '' && formData[field] !== -1 && (isNaN(formData[field]) || parseInt(formData[field]) < -1)) {
        newErrors[field] = 'Limit must be >= -1 (use -1 for unlimited)';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix the form errors', { variant: 'error' });
      return;
    }

    // Check if at least one feature is selected
    const selectedFeatures = Object.keys(features).filter(key => features[key]);
    if (selectedFeatures.length === 0) {
      enqueueSnackbar('Please select at least one feature', { variant: 'error' });
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const normalizedFeatures = {
        loyalty_points: !!features.loyalty_points,
        whatsapp_bill_share: !!features.whatsapp_bill_share,
        ecommerce_website: !!features.ecommerce_website,
        ai_assistant: !!features.ai_assistant,
        shopify_integration: !!features.shopify_integration,
        webapp: !!features.webapp,
        android_app: !!features.android_app,
      };

      // Prepare plan data (UpdatePlanRequest format - all fields optional)
      const planData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        priceMonthly: parseFloat(formData.priceMonthly),
        priceYearly: parseFloat(formData.priceYearly),
        currency: formData.currency,
        trialDays: parseInt(formData.trialDays) || 14,
        maxUsers: unlimitedFlags.maxUsers ? -1 : (formData.maxUsers ? parseInt(formData.maxUsers) : -1),
        maxLocations: unlimitedFlags.maxLocations ? -1 : (formData.maxLocations ? parseInt(formData.maxLocations) : -1),
        maxProducts: unlimitedFlags.maxProducts ? -1 : (formData.maxProducts ? parseInt(formData.maxProducts) : -1),
        maxMonthlyOrders: unlimitedFlags.maxMonthlyOrders ? -1 : (formData.maxMonthlyOrders ? parseInt(formData.maxMonthlyOrders) : -1),
        maxStorageGb: unlimitedFlags.maxStorageGb ? -1 : (formData.maxStorageGb ? parseInt(formData.maxStorageGb) : -1),
        features: normalizedFeatures,
        isPopular: formData.isPopular,
        sortOrder: formData.sortOrder !== '' && formData.sortOrder !== null && !isNaN(formData.sortOrder) ? parseInt(formData.sortOrder, 10) : 0,
      };

      await updatePlan(planId, planData);
      
      enqueueSnackbar('Subscription plan updated successfully!', { variant: 'success' });
      navigate('/superadmin/subscriptions');
    } catch (err) {
      console.error('Error updating plan:', err);
      setError(err.message || 'Failed to update subscription plan');
      enqueueSnackbar(err.message || 'Failed to update subscription plan', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Calculate yearly discount
  const yearlyDiscount = formData.priceMonthly && formData.priceYearly
    ? ((1 - (parseFloat(formData.priceYearly) / (parseFloat(formData.priceMonthly) * 12))) * 100).toFixed(1)
    : 0;

  if (loading) {
    return (
      <SuperAdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/superadmin/subscriptions')} sx={{ color: '#2c3e50' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
              Edit Subscription Plan
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update subscription plan details and features
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Form */}
          <Grid item xs={12} md={8}>
            {/* Basic Information */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#2c3e50' }}>
                Basic Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Plan Code"
                    value={formData.planCode}
                    disabled
                    helperText="Plan code cannot be changed"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Plan Name"
                    value={formData.name}
                    onChange={handleChange('name')}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    placeholder="e.g., Starter Plan"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={handleChange('description')}
                    multiline
                    rows={3}
                    placeholder="Describe this subscription plan..."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isPopular}
                        onChange={(e) => setFormData(prev => ({ ...prev, isPopular: e.target.checked }))}
                      />
                    }
                    label="Mark as Popular"
                  />
                  <Tooltip title="Popular plans are highlighted in the plan selection">
                    <InfoIcon sx={{ ml: 1, fontSize: 16, color: '#6c757d' }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Sort Order"
                    value={formData.sortOrder}
                    onChange={handleChange('sortOrder')}
                    helperText="Lower numbers appear first"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Pricing */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#2c3e50' }}>
                Pricing
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={formData.currency}
                      onChange={handleChange('currency')}
                      label="Currency"
                    >
                      <MenuItem value="USD">USD ($)</MenuItem>
                      <MenuItem value="INR">INR (₹)</MenuItem>
                      <MenuItem value="EUR">EUR (€)</MenuItem>
                      <MenuItem value="GBP">GBP (£)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Monthly Price"
                    value={formData.priceMonthly}
                    onChange={handleChange('priceMonthly')}
                    error={!!errors.priceMonthly}
                    helperText={errors.priceMonthly}
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Yearly Price"
                    value={formData.priceYearly}
                    onChange={handleChange('priceYearly')}
                    error={!!errors.priceYearly}
                    helperText={errors.priceYearly || (yearlyDiscount > 0 ? `Save ${yearlyDiscount}% with yearly` : '')}
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Trial Days"
                    value={formData.trialDays}
                    onChange={handleChange('trialDays')}
                    error={!!errors.trialDays}
                    helperText={errors.trialDays || 'Free trial period in days'}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Usage Limits */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#2c3e50' }}>
                Usage Limits
              </Typography>
              
              <Grid container spacing={2}>
                {[
                  { key: 'maxUsers', label: 'Max Users' },
                  { key: 'maxLocations', label: 'Max Locations' },
                  { key: 'maxProducts', label: 'Max Products' },
                  { key: 'maxMonthlyOrders', label: 'Max Monthly Orders' },
                  { key: 'maxStorageGb', label: 'Max Storage (GB)' },
                ].map(({ key, label }) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label={label}
                        value={unlimitedFlags[key] ? 'Unlimited' : formData[key]}
                        onChange={handleChange(key)}
                        error={!!errors[key]}
                        helperText={errors[key]}
                        disabled={unlimitedFlags[key]}
                        inputProps={{ min: -1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={unlimitedFlags[key]}
                            onChange={handleUnlimitedToggle(key)}
                          />
                        }
                        label="Unlimited"
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Features Selection */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                Features
              </Typography>

              <FormGroup>
                {FEATURE_CATEGORIES.core.features.map((feature) => (
                  <FormControlLabel
                    key={feature.id}
                    control={
                      <Checkbox
                        checked={features[feature.id] || false}
                        onChange={handleFeatureToggle(feature.id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {feature.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Paper>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 3 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/superadmin/subscriptions')}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSubmit}
                disabled={saving}
                sx={{ bgcolor: '#5a9bd4', '&:hover': { bgcolor: '#4a8bc4' } }}
              >
                {saving ? 'Updating...' : 'Update Plan'}
              </Button>
            </Box>
          </Grid>

          {/* Right Column - Summary */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, position: 'sticky', top: 20 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                Plan Summary
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Plan Code</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formData.planCode || '-'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Plan Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formData.name || '-'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Pricing</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formData.currency} {formData.priceMonthly || '0'} / month
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formData.currency} {formData.priceYearly || '0'} / year
                </Typography>
                {yearlyDiscount > 0 && (
                  <Chip label={`Save ${yearlyDiscount}%`} color="success" size="small" sx={{ mt: 1 }} />
                )}
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Selected Features</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {Object.keys(features).filter(key => features[key]).length} features
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </SuperAdminLayout>
  );
};

export default EditSubscriptionPage;

